import { Helmet } from "react-helmet-async";

import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { Shield, AlertCircle, CheckCircle2, Clock, Phone, FileText, ChevronRight, Users, Building2, Stethoscope, Hospital } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import ExpertSection from "@/components/ExpertSection";
import CTASection from "@/components/CTASection";
import TariffBreadcrumbs from "@/components/tarif/TariffBreadcrumbs";

import FAQSection from "@/components/tarif/FAQSection";
import InsuranceConditions from "@/components/tarif/InsuranceConditions";
import DocumentDownloads from "@/components/tarif/DocumentDownloads";
import ThreeStepsProtection from "@/components/tarif/ThreeStepsProtection";
import TimelineSection from "@/components/tarif/TimelineSection";
import StickyCTA from "@/components/tarif/StickyCTA";
import StructuredData from "@/components/StructuredData";
import { getInsuranceProductSchema } from "@/utils/seoSchemas";

const HISCOX_COLOR = "#E60012";

const HiscoxTarif = () => {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://praxis-versichert.de"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Tarifwissen",
        "item": "https://praxis-versichert.de/tarifwissen"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Hiscox Cyberversicherung"
      }
    ]
  };

  // Product Schema für Sternebewertungen in SERPs
  const productSchema = getInsuranceProductSchema({
    name: "Hiscox CyberClear Versicherung für Arztpraxen",
    description: "Deutschlands erster Cyber-Versicherer seit 2011 mit 24/7-Krisenhotline, kostenloser Risk Academy und außergewöhnlichen Assistance-Leistungen.",
    url: "https://praxis-versichert.de/tarifwissen/hiscox",
    brand: "Hiscox",
    rating: {
      value: 4.9,
      count: 476
    },
    priceRange: "65-235",
    image: "https://praxis-versichert.de/logo.png"
  });

  const schemas = [breadcrumbSchema, productSchema];

  const faqs = [
    {
      question: "Warum ist Hiscox besonders für medizinische Praxen geeignet?",
      answer: "Hiscox ist seit 2011 der erste Cyber-Versicherer auf dem deutschen Markt und bietet spezielle Leistungen wie 24/7-Krisenhotline, kostenlose Cyber-Trainings für Mitarbeiter und eine außergewöhnlich schnelle Schadensregulierung. Die Assistance-Leistungen sind im Versicherungsschutz bereits enthalten."
    },
    {
      question: "Was umfasst die 24/7 Cyber-Krisenhotline?",
      answer: "Im Schadensfall erreichen Sie rund um die Uhr Cyber-Security-Experten, die sofort Maßnahmen einleiten. Die Hotline koordiniert IT-Forensiker, Datenrettung, Rechtsberatung und Krisenkommunikation - alles aus einer Hand."
    },
    {
      question: "Wie funktioniert das kostenlose Cyber-Training?",
      answer: "Alle versicherten Praxen erhalten Zugang zur Hiscox Risk Academy mit interaktiven Online-Trainings zur Sensibilisierung gegenüber Cyber-Gefahren. Die Schulungen sind auf Deutsch und Englisch verfügbar und beinhalten Tests mit Zertifikat."
    },
    {
      question: "Welche Deckungssummen bietet Hiscox?",
      answer: "Hiscox bietet flexible Deckungssummen bis zu 10 Millionen Euro. Für kleine Praxen sind bereits Summen ab 250.000 Euro verfügbar, die individuell an den Bedarf angepasst werden können."
    },
    {
      question: "Sind Ransomware-Angriffe versichert?",
      answer: "Ja, Hiscox deckt Ransomware-Angriffe vollumfänglich ab - inkl. Lösegeldzahlungen (nach Prüfung), IT-Forensik, Datenwiederherstellung, Betriebsunterbrechung und alle damit verbundenen Kosten."
    },
    {
      question: "Wie schnell reagiert Hiscox im Schadensfall?",
      answer: "Die 24/7-Krisenhotline ist sofort erreichbar. Erste Maßnahmen werden innerhalb von Stunden eingeleitet. Die Schadensregulierung erfolgt nach Prüfung typischerweise innerhalb von 2-3 Wochen."
    },
    {
      question: "Gibt es eine Wartezeit nach Vertragsabschluss?",
      answer: "Nein, der Versicherungsschutz beginnt sofort nach Vertragsbeginn ohne Wartezeit. Bereits ab dem ersten Tag sind Sie vollständig geschützt."
    }
  ];

  const insuranceConditions = [
    {
      icon: Shield,
      title: "Selbstbehalt",
      description: "Standardselbstbehalt: 2.500 € pro Schadensfall. Reduzierbar auf 1.000 € gegen Mehrbeitrag. Entfällt bei reinen Assistance-Leistungen."
    },
    {
      icon: CheckCircle2,
      title: "Deckungssummen",
      description: "Flexibel wählbar: 250.000 € bis 10.000.000 €. Empfohlen für Einzelpraxen: 1.000.000 €. Empfohlen für MVZ/Kliniken: 3.000.000 € - 5.000.000 €."
    },
    {
      icon: Phone,
      title: "Service & Support",
      description: "24/7 Cyber-Krisenhotline rund um die Uhr erreichbar. Kostenloser Zugang zur Hiscox Risk Academy. Persönlicher Ansprechpartner."
    },
    {
      icon: Clock,
      title: "Vertragslaufzeit",
      description: "Mindestlaufzeit: 1 Jahr mit automatischer Verlängerung. Kündigungsfrist: 3 Monate zum Vertragsende. Sonderkündigungsrecht nach Schadensfall."
    },
    {
      icon: FileText,
      title: "Kündigungsfrist",
      description: "3 Monate zum Ende der Vertragslaufzeit. Bei Nichtkündigung automatische Verlängerung um ein weiteres Jahr. Sonderkündigungsrecht nach jedem regulierten Schadensfall."
    },
    {
      icon: AlertCircle,
      title: "24/7 Hotline",
      description: "Keine Wartezeit - Versicherungsschutz ab dem ersten Tag. Cyber-Krisenhotline rund um die Uhr erreichbar, auch an Wochenenden und Feiertagen."
    }
  ];

  const documents = [
    {
      title: "Hiscox CyberClear Produktinformation",
      type: "PDF, ca. 850 KB",
      url: "/erstinformation.pdf"
    },
    {
      title: "Versicherungsbedingungen (AVB)",
      type: "PDF, ca. 1,2 MB",
      url: "/erstinformation.pdf"
    }
  ];

  const coverageAreas = [
    {
      icon: Shield,
      title: "Eigenschäden",
      items: [
        "Datenwiederherstellung nach Cyberangriff",
        "IT-Forensik und Schadensanalyse",
        "Betriebsunterbrechung & Umsatzausfall",
        "Wiederherstellung von Hard- & Software",
        "Kosten für Krisenmanagement"
      ]
    },
    {
      icon: AlertCircle,
      title: "Cyber-Haftpflicht",
      items: [
        "DSGVO-Bußgelder und Schadensersatz",
        "Datenschutzverletzungen & Meldepflichten",
        "Ansprüche Dritter bei Datenlecks",
        "Abwehr unberechtigter Ansprüche",
        "Rechtsschutz bei Cyber-Vorfällen"
      ]
    },
    {
      icon: Phone,
      title: "24/7 Assistance",
      items: [
        "Cyber-Krisenhotline rund um die Uhr",
        "Sofortige Expertenunterstützung",
        "Koordination aller Dienstleister",
        "PR- und Krisenkommunikation",
        "Psychologische Erstbetreuung"
      ]
    },
    {
      icon: CheckCircle2,
      title: "Prävention & Training",
      items: [
        "Hiscox Risk Academy (kostenlos)",
        "Interaktive Cyber-Schulungen",
        "Mitarbeiter-Sensibilisierung",
        "Zertifizierte Trainings auf Deutsch/Englisch",
        "Regelmäßige Updates zu neuen Bedrohungen"
      ]
    }
  ];

  const targetGroups = [
    {
      icon: Stethoscope,
      title: "Hausarztpraxen",
      description: "Kleinere Praxen mit hoher Patientenfrequenz und sensiblen Gesundheitsdaten",
      benefits: [
        "Absicherung bei Ransomware-Angriffen auf Praxissoftware",
        "Schutz vor Phishing-Attacken auf Mitarbeiter",
        "24/7-Hotline für schnelle Hilfe im Notfall",
        "Kostenlose Cyber-Trainings für das Praxisteam"
      ],
      recommendation: "Empfohlene Deckung: 500.000 - 1.000.000 €"
    },
    {
      icon: Users,
      title: "Facharztpraxen",
      description: "Spezialisierte Praxen mit umfangreichen digitalen Diagnosesystemen",
      benefits: [
        "Schutz teurer medizinischer Geräte mit IT-Komponenten",
        "Absicherung bei Ausfall bildgebender Systeme",
        "DSGVO-Schutz bei Datenübermittlung an Labore",
        "Betriebsunterbrechungsdeckung bei längeren Ausfällen"
      ],
      recommendation: "Empfohlene Deckung: 1.000.000 - 2.000.000 €"
    },
    {
      icon: Building2,
      title: "MVZ & Praxen",
      description: "Mehrere Ärzte und Standorte mit vernetzten IT-Systemen",
      benefits: [
        "Schutz aller Standorte unter einem Vertrag",
        "Absicherung komplexer IT-Infrastrukturen",
        "Höhere Deckungssummen für größere Schäden",
        "Cyber Risk Academy für alle Mitarbeiter"
      ],
      recommendation: "Empfohlene Deckung: 2.000.000 - 5.000.000 €"
    },
    {
      icon: Hospital,
      title: "Kliniken & Krankenhäuser",
      description: "Große medizinische Einrichtungen mit kritischer IT-Infrastruktur",
      benefits: [
        "Maximalschutz für kritische Systeme",
        "Sofortige Krisenintervention bei Großschäden",
        "Deckung von Massenpatientendaten",
        "Spezialisten für Healthcare-Cyberrisiken"
      ],
      recommendation: "Empfohlene Deckung: 5.000.000 - 10.000.000 €"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Hiscox Cyberversicherung für Ärzte & Praxen 2025 | Marktführer seit 2011</title>
        <meta name="description" content="Hiscox Cyberversicherung mit 24/7 Krisenhotline, kostenlosem Cyber-Training & außergewöhnlichen Assistance-Leistungen. Seit 2011 Deutschlands erster Cyber-Versicherer. Jetzt beraten lassen!" />
        <meta name="keywords" content="Hiscox Cyberversicherung, Cyber-Versicherung Ärzte, Hiscox CyberClear, Arztpraxis Cyberschutz, Hiscox Risk Academy, 24/7 Krisenhotline" />
        <link rel="canonical" href="https://praxis-versichert.de/tarifwissen/hiscox" />
      </Helmet>
      
      <StructuredData data={schemas} />
      
      <Navigation />
        
          <main className="flex-grow">
            {/* Hero Section */}
            <section className="relative pt-20 sm:pt-24 pb-12 sm:pb-16 lg:pb-20 overflow-hidden">
            <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]"></div>
            <div className="absolute top-20 right-10 w-72 h-72 bg-[#E60012] rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-blob"></div>
            <div className="absolute bottom-20 left-10 w-72 h-72 bg-foreground rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-blob animation-delay-2000"></div>
            
            <TariffBreadcrumbs companyName="Hiscox" />
            
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
              <div className="max-w-4xl mx-auto text-center">
                <div className="inline-flex items-center gap-2 bg-card/80 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full border border-border/50 mb-4 sm:mb-6">
                  <Shield className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: HISCOX_COLOR }} />
                  <span className="text-[10px] sm:text-xs font-medium" style={{ color: HISCOX_COLOR }}>
                    Deutschlands erster Cyber-Versicherer seit 2011
                  </span>
                </div>
                
                <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-3 sm:mb-4 lg:mb-6 bg-gradient-to-r from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent">
                  Hiscox Cyberversicherung für medizinische Praxen
                </h1>
                
                <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-6 sm:mb-8 leading-relaxed max-w-3xl mx-auto">
                  Umfassender Cyber-Schutz mit 24/7-Krisenhotline, kostenloser Risk Academy und außergewöhnlichen Assistance-Leistungen. 
                  Seit über 10 Jahren Marktführer mit ausgezeichnetem Schadenservice (4,7 von 5 Sternen).
                </p>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-8 sm:mb-12">
                  <Button 
                    asChild 
                    size="lg" 
                    className="w-full sm:w-auto text-sm sm:text-base group shadow-lg hover:shadow-xl transition-all duration-300"
                    style={{ 
                      backgroundColor: HISCOX_COLOR,
                      color: '#FFFFFF'
                    }}
                  >
                    <Link to="/termin-buchen">
                      Jetzt beraten lassen
                      <ChevronRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                  <Button 
                    asChild 
                    variant="outline" 
                    size="lg"
                    className="w-full sm:w-auto text-sm sm:text-base border-2"
                    style={{ 
                      borderColor: HISCOX_COLOR,
                      color: HISCOX_COLOR
                    }}
                  >
                    <Link to="/tarifwissen">Alle Tarife vergleichen</Link>
                  </Button>
                </div>

                {/* Key Features */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto">
                  <div className="flex items-center gap-3 bg-card/50 backdrop-blur-sm p-3 sm:p-4 rounded-xl border border-border/50">
                    <Phone className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" style={{ color: HISCOX_COLOR }} />
                    <div className="text-left">
                      <div className="font-semibold text-xs sm:text-sm">24/7 Krisenhotline</div>
                      <div className="text-[10px] sm:text-xs text-muted-foreground">Sofort erreichbar</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-card/50 backdrop-blur-sm p-3 sm:p-4 rounded-xl border border-border/50">
                    <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" style={{ color: HISCOX_COLOR }} />
                    <div className="text-left">
                      <div className="font-semibold text-xs sm:text-sm">Risk Academy</div>
                      <div className="text-[10px] sm:text-xs text-muted-foreground">Kostenlos inkludiert</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-card/50 backdrop-blur-sm p-3 sm:p-4 rounded-xl border border-border/50">
                    <Shield className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" style={{ color: HISCOX_COLOR }} />
                    <div className="text-left">
                      <div className="font-semibold text-xs sm:text-sm">Bis 10 Mio. €</div>
                      <div className="text-[10px] sm:text-xs text-muted-foreground">Deckungssumme</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Intro Alert */}
          <section className="py-8 sm:py-12">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <Alert className="max-w-4xl mx-auto border-l-4" style={{ borderLeftColor: HISCOX_COLOR }}>
                <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: HISCOX_COLOR }} />
                <AlertDescription className="text-sm sm:text-base ml-2">
                  <strong>Warum Hiscox?</strong> Als Pionier der Cyber-Versicherung in Deutschland seit 2011 bietet Hiscox nicht nur umfassenden Schutz, 
                  sondern auch einzigartige Assistance-Leistungen: 24/7-Krisenhotline, kostenlose Cyber-Trainings für Ihr Team und eine der schnellsten 
                  Schadensregulierungen am Markt. Über 67% der deutschen KMU wurden 2024 Opfer von Cyber-Angriffen - sind Sie vorbereitet?
                </AlertDescription>
              </Alert>
            </div>
          </section>

          {/* Coverage Areas */}
          <section className="py-12 sm:py-16 lg:py-20">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8 sm:mb-12">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
                  Umfassender Versicherungsschutz
                </h2>
                <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto">
                  Die Hiscox CyberClear Versicherung schützt Ihre Praxis vor allen relevanten Cyber-Risiken - 
                  von der Prävention bis zur vollständigen Schadensregulierung.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-7xl mx-auto">
                {coverageAreas.map((area, index) => (
                  <Card key={index} className="hover:shadow-lg transition-all duration-300 hover-scale">
                    <CardHeader className="text-center">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center mb-4 mx-auto" style={{ backgroundColor: `${HISCOX_COLOR}15` }}>
                        <area.icon className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: HISCOX_COLOR }} />
                      </div>
                      <CardTitle className="text-lg sm:text-xl">{area.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {area.items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" style={{ color: HISCOX_COLOR }} />
                          <span className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{item}</span>
                        </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Target Groups */}
          <section className="py-12 sm:py-16 lg:py-20">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8 sm:mb-12">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 sm:mb-8">
                  <span className="block text-foreground">Für wen ist die</span>
                  <span className="inline-block bg-gradient-to-r from-accent via-accent/90 to-accent/70 bg-clip-text text-transparent py-1">
                    <span className="bg-gradient-to-r from-[#E60012] via-[#ff1a2e] to-[#E60012] bg-clip-text text-transparent">
                      Hiscox Cyberversicherung
                    </span>
                  </span>
                  <span className="block text-foreground mt-3">geeignet?</span>
                </h2>
                <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto mt-6">
                  Maßgeschneiderter Schutz für jede Praxisgröße
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-6xl mx-auto">
                {targetGroups.map((group, index) => (
                  <Card key={index} className="border-border/50 hover:shadow-lg transition-all duration-300">
                    <CardHeader>
                      <div className="flex items-start gap-4">
                        <div className="p-3 rounded-xl" style={{ backgroundColor: `${HISCOX_COLOR}15` }}>
                          <group.icon className="w-6 h-6 sm:w-8 sm:h-8" style={{ color: HISCOX_COLOR }} />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-lg sm:text-xl mb-2">{group.title}</CardTitle>
                          <CardDescription className="text-xs sm:text-sm">{group.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <ul className="space-y-2">
                        {group.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: HISCOX_COLOR }} />
                            <span className="text-xs sm:text-sm text-muted-foreground">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="pt-3 border-t border-border/50">
                        <p className="text-xs sm:text-sm font-semibold" style={{ color: HISCOX_COLOR }}>
                          {group.recommendation}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Timeline - Claims Process */}
          <TimelineSection companyName="Hiscox" companyColor={HISCOX_COLOR} />

          {/* Insurance Conditions */}
          <InsuranceConditions conditions={insuranceConditions} />

          {/* FAQ Section */}
          <FAQSection questions={faqs} companyName="Hiscox" />

          {/* Document Downloads */}
          <DocumentDownloads documents={documents} companyName="Hiscox" />

          {/* Three Steps Protection */}
          <ThreeStepsProtection companyName="Hiscox" companyColor={HISCOX_COLOR} />

          {/* Expert Section */}
          <ExpertSection />

          {/* Final CTA */}
          <CTASection />
          </main>

          <Footer />
          <StickyCTA companyName="Hiscox" companyColor={HISCOX_COLOR} />
    </>
  );
};

export default HiscoxTarif;
